import { z } from "zod";
import { requestId, SeriesSettings, TableSettings } from "./room.js";
import { isTableDesign } from "./tables.js";
export const FriendOp = z.object({
    op: z.enum(["request", "accept", "decline", "cancel", "remove", "block", "unblock"]),
    target: z.string().regex(/^DG-[a-f0-9]{24}$/),
    revision: z.string(),
    requestId,
});
export const ClubRuleLocks = z.array(z.enum(["minimum50", "oneCardAlert", "turnSeconds", "minus100"])).max(4);
export const ClubOp = z.discriminatedUnion("op", [
    z.object({ op: z.literal("create"), name: z.string().min(2).max(40), settings: TableSettings.partial().optional(), locks: ClubRuleLocks.optional(), seriesSettings: SeriesSettings.optional(), photo: z.string().max(3 * 1024 * 1024).nullable().optional(), requestId }),
    z.object({ op: z.literal("join"), code: z.string(), requestId }),
    z.object({ op: z.literal("accept"), code: z.string(), publicId: z.string(), requestId }),
    z.object({ op: z.literal("deny"), code: z.string(), publicId: z.string(), requestId }),
    z.object({ op: z.literal("role"), code: z.string(), publicId: z.string(), role: z.enum(["admin", "member"]), requestId }),
    z.object({ op: z.literal("remove"), code: z.string(), publicId: z.string(), requestId }),
    z.object({ op: z.literal("leave"), code: z.string(), requestId }),
    /** Withdraw your own join request while it waits. */
    z.object({ op: z.literal("cancel"), code: z.string(), requestId }),
    z.object({ op: z.literal("settings"), code: z.string(), name: z.string().min(2).max(40).optional(),
        /** The club's table design (D-099): a TABLE_DESIGNS id everyone at its tables sees, or null for each player's own. */
        tableDesign: z.string().max(32).refine((v) => isTableDesign(v), "Pick one of the table designs.").nullable().optional(),
        settings: TableSettings.partial().optional(), locks: ClubRuleLocks.optional(), seriesSettings: SeriesSettings.optional(), photo: z.string().max(3 * 1024 * 1024).nullable().optional(), requestId }),
    z.object({ op: z.literal("disband"), code: z.string(), confirm: z.literal(true), requestId }),
]);
export const CreateClubTable = z.object({
    name: z.string().max(40).optional(),
    rules: TableSettings.pick({ minimum50: true, oneCardAlert: true, turnSeconds: true, minus100: true }).partial().optional(),
    series: SeriesSettings.optional(),
    /** Fixed games and target score only; open play always shuffles seats between rounds. */
    seatOrder: z.enum(["fixed", "shuffled"]).optional(),
    requestId,
});
/**
 * Tournaments (D-134, the Tournaments document). Every tournament is a stage knockout: tables of 4 (some of 3 when the
 * players don't divide), the top 2 at a table of 4 and the winner at a table of 3 go on, the best of the rest ("lucky
 * losers") fill the next stage to a multiple of 4, and the last 4 play the final table for 1st to 4th.
 * Hosts: the app team (`host.kind` "app", from the admin console) or a club's owner and admins (always free to enter).
 */
export const TOURNAMENT_SIZES = [4, 8, 16, 32, 64, 128, 256];
const Size = z.number().int().refine((n) => TOURNAMENT_SIZES.includes(n), "Pick a player limit of 4, 8, 16, 32, 64, 128 or 256.");
export const TOURNAMENT_TURN_SECONDS = [15, 20, 30, 45];
/** The game settings the host picks; every table of the tournament plays by them, and they lock when registration opens. */
export const TournamentRules = z.object({
    minimum50: z.boolean().default(true),
    oneCardAlert: z.boolean().default(true),
    minus100: z.boolean().default(false),
    seatOrder: z.enum(["fixed", "shuffled"]).default("shuffled"),
    turnSeconds: z.number().int().refine((n) => TOURNAMENT_TURN_SECONDS.includes(n), "Pick 15, 20, 30 or 45 seconds a turn.").default(20),
});
export const DEFAULT_TOURNAMENT_RULES = { minimum50: true, oneCardAlert: true, minus100: false, seatOrder: "shuffled", turnSeconds: 20 };
/** One prize line: places `from`..`to` each get it. 1st also always gets the champion badge and title. Prize Boli is locked (Shop only). */
export const TournamentPrize = z.object({
    from: z.number().int().min(1).max(256),
    to: z.number().int().min(1).max(256),
    boli: z.number().int().min(0).max(1_000_000).default(0),
    tokens: z.number().int().min(0).max(100_000).default(0),
    /** A shop item given outright (a card back, a table design, a throw pack). */
    itemId: z.string().regex(/^[a-z0-9-]{2,40}$/).nullable().default(null),
    /** D-136: an entry ticket: for this tournament's series, or any Sit & Go / scheduled app tournament up to `maxEntry` tokens; good for `days`. */
    ticket: z.object({ scope: z.enum(["series", "sng", "scheduled"]), maxEntry: z.number().int().min(0).max(10_000).default(0), days: z.number().int().min(1).max(90).default(14) }).nullable().default(null),
});
export const TournamentType = z.enum(["scheduled", "sng"]);
/** How an app tournament comes back: once, a week or a day after its start, or (Sit & Go) a fresh copy as soon as it fills. */
export const TournamentRepeat = z.enum(["once", "weekly", "daily", "refill"]);
const TournamentFields = {
    name: z.string().trim().min(3).max(60),
    type: TournamentType.default("scheduled"),
    /** Required for Scheduled; ignored for Sit & Go. */
    startsAt: z.number().int().nullable().default(null),
    size: Size,
    stageRounds: z.number().int().min(1).max(10).default(4),
    finalRounds: z.number().int().min(1).max(10).default(6),
    rules: TournamentRules.default(DEFAULT_TOURNAMENT_RULES),
};
/** D-137: a sponsor and their real-world prizes (which make entry free), and an edition card back for the winners. */
const SponsorFields = {
    sponsorId: z.string().max(40).nullable().default(null),
    sponsorPrizes: z.array(z.object({ from: z.number().int().min(1).max(256), to: z.number().int().min(1).max(256), text: z.string().trim().min(2).max(80) })).max(10).default([]),
    editionId: z.string().regex(/^back-edition-[a-f0-9]{8,32}$/).nullable().default(null),
    editionTo: z.enum(["first", "top4", "final"]).default("first"),
};
/** POST /clubs/:code/tournaments: a club tournament. Always free to enter (D-134). */
/**
 * D-135: club tournament rules (Admin › Club tournaments): free tournaments a month per club (shared by the owner and
 * admins, reset on the 1st, Malé time), the free player limit, the Boli for each extra one and for bigger limits.
 */
export const ClubTournamentRules = z.object({
    freePerMonth: z.number().int().min(0).max(50).default(2),
    freeLimit: z.union([z.literal(4), z.literal(8), z.literal(16), z.literal(32)]).default(16),
    extraBoli: z.number().int().min(0).max(1_000_000).default(300),
    /** Boli for each limit above the free one, by player limit ("32", "64", …). A missing limit can't be bought. */
    limitBoli: z.record(z.string(), z.number().int().min(0).max(1_000_000)).default({ "32": 200, "64": 500, "128": 1200, "256": 2500 }),
});
export const DEFAULT_CLUB_TOURNAMENT_RULES = { freePerMonth: 2, freeLimit: 16, extraBoli: 300, limitBoli: { "32": 200, "64": 500, "128": 1200, "256": 2500 } };
/** Boli a club pays for this player limit: 0 up to the free limit, null when the limit isn't on offer. */
export function clubLimitCost(rules, size) {
    if (size <= rules.freeLimit)
        return 0;
    const c = rules.limitBoli[String(size)];
    return typeof c === "number" ? c : null;
}
/** A prize the club hands over itself (cash, a dinner): verified clubs only; DIGU asks winners whether they got it. */
export const ClubPrize = z.object({ from: z.number().int().min(1).max(256), to: z.number().int().min(1).max(256), text: z.string().trim().min(2).max(80) });
/** In-app prizes a club offers: Boli and tokens, held from the host's balance when created (D-135). */
export const ClubInAppPrize = z.object({ from: z.number().int().min(1).max(256), to: z.number().int().min(1).max(256), boli: z.number().int().min(0).max(1_000_000).default(0), tokens: z.number().int().min(0).max(100_000).default(0) });
/** POST /clubs/:code/tournaments: a club tournament. Always free to enter. `membersOnly` false (public) needs a verified club. */
export const CreateTournament = z.object({
    ...TournamentFields, membersOnly: z.boolean().default(true),
    prizes: z.array(ClubInAppPrize).max(20).default([]),
    clubPrizes: z.array(ClubPrize).max(10).default([]),
    requestId,
});
/** POST /tournaments/:id/limit: the host raises a club tournament's player limit while registration is open, paying the difference. */
export const RaiseTournamentLimit = z.object({ size: Size, requestId });
/** POST /admin/tournaments/new: an app tournament. `publish` false saves a draft that can still change. */
export const CreateAppTournament = z.object({
    ...TournamentFields,
    entryTokens: z.number().int().min(0).max(10_000).default(0),
    prizes: z.array(TournamentPrize).max(20).default([]),
    repeat: TournamentRepeat.default("once"),
    tableDesign: z.string().regex(/^[a-z0-9-]{2,40}$/).nullable().default(null),
    featured: z.boolean().default(false),
    publish: z.boolean().default(true),
    ...SponsorFields,
    requestId,
});
export const SPONSOR_CLIP_MAX_BYTES = 4 * 1024 * 1024;
export const SPONSOR_CLIP_MAX_SECONDS = 15;
/** POST /admin/tournaments/:id/edit: a draft changes freely; once published only the name, and stopping a repeat. */
export const EditTournament = z.object({
    name: TournamentFields.name.optional(),
    startsAt: z.number().int().optional(),
    size: Size.optional(),
    stageRounds: TournamentFields.stageRounds.optional(),
    finalRounds: TournamentFields.finalRounds.optional(),
    rules: TournamentRules.optional(),
    entryTokens: z.number().int().min(0).max(10_000).optional(),
    prizes: z.array(TournamentPrize).max(20).optional(),
    repeat: TournamentRepeat.optional(),
    tableDesign: z.string().regex(/^[a-z0-9-]{2,40}$/).nullable().optional(),
    featured: z.boolean().optional(),
    publish: z.boolean().optional(),
    sponsorId: z.string().max(40).nullable().optional(),
    sponsorPrizes: SponsorFields.sponsorPrizes.optional(),
    editionId: SponsorFields.editionId.optional(),
    editionTo: SponsorFields.editionTo.optional(),
    requestId,
});
export function tournamentEditStage(status) {
    return status === "draft" ? "full" : status === "scheduled" ? "name" : "none";
}
/** The stages a tournament of `players` plays (D-134). Pure, shared by the server, the Structure tab and the admin preview. */
export function tournamentStructure(players, stageRounds, finalRounds) {
    const out = [];
    let n = players, stage = 1;
    if (n < 2)
        return out;
    while (n > 4) {
        const sizes = stageTableSizes(n), advancing = sizes.reduce((a, s) => a + (s === 4 ? 2 : 1), 0);
        const next = advancing <= 4 ? 4 : Math.ceil(advancing / 4) * 4;
        out.push({ stage, final: false, players: n, tables: sizes.length, tablesOfThree: sizes.filter((s) => s < 4).length, rounds: stageRounds, movingOn: Math.min(next, n) });
        n = Math.min(next, n);
        stage++;
    }
    out.push({ stage, final: true, players: n, tables: 1, tablesOfThree: n < 4 ? 1 : 0, rounds: finalRounds, movingOn: 0 });
    return out;
}
/** Table sizes for one stage of n (> 4) players: fours, with threes (or a 3 and a 2 for five) to take up the rest. */
export function stageTableSizes(n) {
    if (n <= 4)
        return [n];
    if (n === 5)
        return [3, 2];
    const threes = (4 - (n % 4)) % 4;
    return [...Array((n - threes * 3) / 4).fill(4), ...Array(threes).fill(3)];
}
/** A rough length for the Structure tab: about 6 minutes a round at 20 s turns, scaled by the turn time, plus a minute between stages. */
export function tournamentMinutes(structure, turnSeconds) {
    const perRound = 6 * (0.5 + turnSeconds / 40);
    return Math.round(structure.reduce((m, s) => m + s.rounds * perRound + 1, 0));
}
export const RankingsConfig = z.object({
    version: z.number().int(),
    seasonMode: z.enum(["monthlyMaldives", "monthlyUTC"]),
    tiePolicy: z.enum(["split"]),
    rewards: z.array(z.object({ from: z.number().int().min(1), to: z.number().int().min(1).max(100), boli: z.number().int().min(0), tokens: z.number().int().min(0), badge: z.string().nullable() })).max(20),
});
/** D-136: what each membership gives for tournaments each month (Admin › Membership benefits). */
export const TournamentBenefits = z.object({
    sng: z.number().int().min(0).max(20).default(0), sngMax: z.number().int().min(0).max(10_000).default(10),
    scheduled: z.number().int().min(0).max(20).default(0), scheduledMax: z.number().int().min(0).max(10_000).default(20),
    clubExtra: z.number().int().min(0).max(20).default(0),
});
export const DEFAULT_TOURNAMENT_BENEFITS = {
    gold: { sng: 1, sngMax: 10, scheduled: 0, scheduledMax: 20, clubExtra: 0 },
    black: { sng: 2, sngMax: 10, scheduled: 1, scheduledMax: 20, clubExtra: 0 },
    signature: { sng: 2, sngMax: 10, scheduled: 2, scheduledMax: 20, clubExtra: 1 },
};
/** Admin › Tournaments settings: the size clubs may host without Signature, and the featured tournaments. */
export const SponsorshipConfig = z.object({
    version: z.number().int(),
    monthlyBudgetBoli: z.number().int().min(0).max(10_000_000).default(0),
    maxClubTournamentWithoutMembership: z.union([z.literal(4), z.literal(8), z.literal(16), z.literal(32)]).default(8),
    /** D-135: replaces the Signature size rule. */
    club: ClubTournamentRules.default(DEFAULT_CLUB_TOURNAMENT_RULES),
    /** D-136: monthly tournament benefits per membership. */
    vip: z.object({ gold: TournamentBenefits, black: TournamentBenefits, signature: TournamentBenefits }).default(DEFAULT_TOURNAMENT_BENEFITS),
    featured: z.array(z.string()).max(10),
    prizes: z.record(z.string(), z.number().int().min(0)).default({}),
});
export const BADGES = ["tenofsuit", "perfect", "firstdraw", "cleansweep", "comeback", "champion", "finaltable", "champion5", "champion25"];
export const BADGE_TITLES = {
    tenofsuit: "Ten of a suit", perfect: "Perfect", firstdraw: "First draw", cleansweep: "Clean sweep", comeback: "Comeback", champion: "Champion",
    finaltable: "Final table", champion5: "Five-time champion", champion25: "Legend of the table",
};
/** POST /clubs/:code/verification: the owner's application. Pictures and documents as data URLs (JPEG, PNG, WebP; PDF for other documents). */
export const SubmitVerification = z.object({
    legalName: z.string().trim().min(3).max(80),
    idNumber: z.string().trim().min(3).max(30),
    front: z.string().max(8 * 1024 * 1024),
    back: z.string().max(8 * 1024 * 1024),
    other: z.array(z.string().max(8 * 1024 * 1024)).max(3).default([]),
    requestId,
});
export const AnswerPrizeCheck = z.object({ answer: z.enum(["yes", "no"]), report: z.string().trim().max(500).default(""), requestId });
export const PRIZE_QUIET_DAYS = 7;
/** D-138: POST /admin/prize-reports/:id/:publicId/resolve; the note is what the winner is told. */
export const ResolvePrizeReport = z.object({ note: z.string().trim().max(300).default("") });
/** D-136: POST /tournaments/:id/register; a ticket pays the entry instead of tokens. */
export const RegisterTournament = z.object({ requestId, ticketId: z.string().max(40).optional() });
/** D-137: Admin › Sponsors. */
export const SponsorInput = z.object({
    name: z.string().trim().min(2).max(40),
    color: z.string().regex(/^#[0-9A-Fa-f]{6}$/).default("#22A6C8"),
    about: z.string().trim().max(140).default(""),
    link: z.string().trim().url().max(300).nullable().default(null),
    startsAt: z.number().int().nullable().default(null),
    endsAt: z.number().int().nullable().default(null),
    placements: z.object({ band: z.boolean(), felt: z.boolean(), waiting: z.boolean(), results: z.boolean(), home: z.boolean() }).default({ band: true, felt: true, waiting: true, results: true, home: false }),
    slotText: z.string().trim().max(80).default(""),
    slotButton: z.string().trim().max(24).default(""),
    requestId,
});
//# sourceMappingURL=community.js.map